/**
 * Catálogo canónico de pasos del funnel (contrato 3.1, `steps` de
 * contract/front-event-v3.catalog.json; el test compara pasos y orden con él).
 *
 * Cada pantalla por la que pasa el cliente tiene un `step` estable en snake_case.
 * El paso se saca de la RUTA (la pantalla que de verdad ve el cliente); el estado
 * del lead (`contact_lead.status`, o el que calcula ms-agl en native) viaja
 * aparte como `raw_status`. El CRM y Atlas usan este catálogo para las etiquetas
 * y los umbrales por paso: si se añade o se renombra un paso, se cambia también
 * el contrato común.
 *
 * `order` ordena el recorrido para saber si un cambio de pantalla es avanzar
 * (`step_completed`) o volver atrás (`step_back`). Las pantallas laterales
 * (login, resumen al volver, landings de partner…) no tienen orden: entrar o
 * salir de ellas solo emite `step_viewed`.
 */

import type { Market } from "./events";

export type { Market };

/** Mercados del catálogo; «IT_NATIVE» es Italia en modo native (sin contact_lead). */
export type CatalogMarket = "ES" | "AXA_WL" | "IT" | "IT_NATIVE" | "FR";

export type CatalogStepId =
  | "quote"
  | "pause"
  | "product_questions"
  | "medical_questions"
  | "rejected"
  | "telesubscription"
  | "max_capital"
  | "waiting_advisor"
  | "upsell"
  | "payment"
  | "confirmation"
  | "nationality"
  | "address"
  | "beneficiaries"
  | "beneficiaries_clause"
  | "beneficiaries_types"
  | "beneficiaries_physical_person"
  | "beneficiaries_banking_entity"
  | "beneficiaries_legal_person"
  | "beneficiaries_capital"
  | "idcard"
  | "client_consents"
  | "legal_note"
  | "card_validation"
  | "waiting_validation"
  | "signature"
  | "agent_confirmation"
  | "waiting_signature"
  | "waiting_customer"
  | "decision"
  | "login"
  | "dashboard"
  | "renewal_confirmation"
  | "partner_landing"
  | "resume_velogica"
  | "other";

/** Pasos de la calculadora: `calculator_<n>` (la pantalla de precio es `quote`). */
export type CalculatorStepId = `calculator_${number}`;

export type StepId = CatalogStepId | CalculatorStepId;

export interface StepDefinition {
  step: CatalogStepId;
  label: string;
  /** Rutas de la app (patrones con `:param`). */
  routes: string[];
  /** Estados del lead que llevan a esta pantalla (el primero manda en `stepForStatus`). */
  rawStatuses: string[];
  markets: CatalogMarket[];
  /** Posición en el recorrido; null = pantalla lateral. */
  order: number | null;
}

const ALL: CatalogMarket[] = ["ES", "AXA_WL", "IT", "IT_NATIVE", "FR"];
const MONOLITH: CatalogMarket[] = ["ES", "AXA_WL", "IT", "FR"];
const ES_LIKE: CatalogMarket[] = ["ES", "AXA_WL"];
const ITALY: CatalogMarket[] = ["IT", "IT_NATIVE"];

/** Las cuatro variantes de la pasarela de pago (A/B y native comparten pasos). */
const paymentRoutes = (n: number): string[] => [
  `/payment/${n}`,
  `/paymentChallenger/${n}`,
  `/paymentProposed/${n}`,
  `/paymentNew/${n}`,
];

/**
 * Pasos de la calculadora antes del precio. Viven fuera del catálogo porque su
 * número depende del mercado (FR tiene 8 antes del precio; el resto, 3).
 */
export const CALCULATOR_STEP_LABELS: Record<number, string> = {
  1: "Calculadora: datos personales (fecha de nacimiento y sexo; IT también fumador)",
  2: "Calculadora: capital (FR: fumador)",
  3: "Calculadora: datos de contacto, se crea el lead (FR: profesión)",
  4: "Calculadora FR: kilómetros al año",
  5: "Calculadora FR: trabajo en altura",
  6: "Calculadora FR: carga de peso",
  7: "Calculadora FR: capital",
  8: "Calculadora FR: datos de contacto, se crea el lead",
};

export const STEP_CATALOG: StepDefinition[] = [
  {
    step: "quote",
    label: "Precio (cotización)",
    routes: ["/calculator/4", "/calculatorChallenger/4", "/calculator/9 (FR)"],
    rawStatuses: [],
    markets: ALL,
    order: 40,
  },
  {
    step: "pause",
    label: "Pausa antes de las preguntas",
    routes: ["/pause", "/pauseAB"],
    rawStatuses: [],
    markets: ES_LIKE,
    order: 45,
  },
  {
    step: "product_questions",
    label: "Preguntas de producto",
    routes: ["/productQuestions"],
    rawStatuses: ["product_questions_pending", "prestack_pending"],
    markets: MONOLITH,
    order: 50,
  },
  {
    step: "medical_questions",
    label: "Cuestionario de salud",
    routes: ["/medicalQuestions"],
    rawStatuses: ["questions_pending"],
    markets: ALL,
    order: 60,
  },
  {
    step: "rejected",
    label: "Sin cobertura (rechazado)",
    routes: ["/notcoverage"],
    rawStatuses: ["rejected"],
    markets: ALL,
    order: 65,
  },
  {
    step: "telesubscription",
    label: "Telesuscripción (la termina un agente)",
    routes: ["/telesubscription"],
    rawStatuses: ["telesubscription"],
    markets: MONOLITH,
    order: 65,
  },
  {
    step: "max_capital",
    label: "Capital por encima del máximo",
    routes: ["/maxCapital"],
    rawStatuses: [],
    markets: MONOLITH,
    order: null,
  },
  {
    step: "waiting_advisor",
    label: "Esperando a que el comercial cierre la oferta",
    routes: ["/waiting-advisor"],
    rawStatuses: ["advisor_pending"],
    markets: ITALY,
    order: null,
  },
  {
    step: "upsell",
    label: "Mejora de capital (upsell)",
    routes: paymentRoutes(1),
    rawStatuses: ["upsell_pending"],
    markets: ALL,
    order: 70,
  },
  {
    step: "payment",
    label: "Método de pago",
    routes: paymentRoutes(3),
    rawStatuses: ["payment_pending", "paymentmethodpending_pending"],
    markets: ALL,
    order: 80,
  },
  {
    step: "confirmation",
    label: "Confirmación tras el método de pago",
    routes: ["/confirmation"],
    rawStatuses: [],
    markets: ALL,
    order: 85,
  },
  {
    step: "nationality",
    label: "Nacionalidad, residencia y documento",
    routes: ["/coverage/0"],
    rawStatuses: ["nationality_pending"],
    markets: ALL,
    order: 90,
  },
  {
    step: "address",
    label: "Dirección",
    routes: ["/coverage/1"],
    rawStatuses: ["address_pending"],
    markets: ALL,
    order: 100,
  },
  {
    step: "beneficiaries",
    label: "Beneficiarios: modalidad",
    routes: ["/coverage/2", "/beneficiary (IT)"],
    rawStatuses: ["beneficiaries_pending"],
    markets: ALL,
    order: 110,
  },
  {
    step: "beneficiaries_clause",
    label: "Beneficiarios: cláusula pignoraticia o exclusiva",
    routes: ["/beneficiaries/designation/:modality"],
    rawStatuses: ["beneficiaries_pending"],
    markets: ES_LIKE,
    order: 112,
  },
  {
    step: "beneficiaries_types",
    label: "Beneficiarios: tipo (física, jurídica, banco)",
    routes: ["/beneficiaries/1"],
    rawStatuses: ["beneficiariestypes_pending"],
    markets: ["ES", "AXA_WL", "FR"],
    order: 114,
  },
  {
    step: "beneficiaries_physical_person",
    label: "Beneficiarios: persona física",
    routes: ["/beneficiaries/2"],
    rawStatuses: ["physicalperson_pending"],
    markets: ["ES", "AXA_WL", "FR"],
    order: 116,
  },
  {
    step: "beneficiaries_banking_entity",
    label: "Beneficiarios: entidad bancaria",
    routes: ["/beneficiaries/3"],
    rawStatuses: ["bankingentity_pending"],
    markets: ["ES", "AXA_WL", "FR"],
    order: 118,
  },
  {
    step: "beneficiaries_legal_person",
    label: "Beneficiarios: persona jurídica",
    routes: ["/beneficiaries/4"],
    rawStatuses: ["legalperson_pending"],
    markets: ["ES", "AXA_WL", "FR"],
    order: 120,
  },
  {
    step: "beneficiaries_capital",
    label: "Beneficiarios: reparto del capital",
    routes: ["/beneficiaries/5"],
    rawStatuses: [],
    markets: ["ES", "AXA_WL", "FR"],
    order: 122,
  },
  {
    step: "idcard",
    label: "Documento de identidad (fotos y selfie)",
    routes: ["/coverage/7"],
    rawStatuses: ["idcard_pending"],
    markets: ALL,
    order: 130,
  },
  {
    step: "client_consents",
    label: "Consentimientos del cliente (alta del comercial)",
    routes: ["/consents"],
    rawStatuses: ["consents_pending"],
    markets: ITALY,
    order: 135,
  },
  {
    step: "legal_note",
    label: "Nota legal y cobro de validación",
    routes: paymentRoutes(4),
    rawStatuses: ["legalnote_pending"],
    markets: ALL,
    order: 140,
  },
  {
    step: "card_validation",
    label: "Vuelta de la verificación 3D Secure",
    routes: ["/card-validation"],
    rawStatuses: [],
    markets: ALL,
    order: 145,
  },
  {
    step: "waiting_validation",
    label: "Pago pendiente de validación",
    routes: ["/waitingvalidation"],
    rawStatuses: ["validation_pending"],
    markets: ALL,
    order: 150,
  },
  {
    step: "signature",
    label: "Firma del contrato",
    routes: paymentRoutes(5),
    rawStatuses: ["signature_pending", "payment_validation_pending"],
    markets: ALL,
    order: 160,
  },
  {
    step: "agent_confirmation",
    label: "Firma: la termina un agente",
    routes: ["/agent-confirmation"],
    rawStatuses: [],
    markets: ALL,
    order: 165,
  },
  {
    step: "waiting_signature",
    label: "Después de firmar (o firma aplazada)",
    routes: ["/waitingsignature"],
    rawStatuses: ["signature_pending", "payment_validation_pending"],
    markets: ALL,
    order: 170,
  },
  {
    step: "dashboard",
    label: "Área de cliente",
    routes: ["/dashboard/:section"],
    rawStatuses: [
      "complete",
      "ops_review",
      "it_review",
      "insurer_review",
      "ready_to_send",
      "docs_pending",
      "csv_pending",
      "claim",
      "canceled",
    ],
    markets: ALL,
    order: 200,
  },
  {
    step: "waiting_customer",
    label: "FR: el equipo contactará con el cliente",
    routes: ["/waiting-customer"],
    rawStatuses: [],
    markets: ["FR"],
    order: null,
  },
  {
    step: "decision",
    label: "Resumen al volver",
    routes: ["/decision"],
    rawStatuses: [],
    markets: ALL,
    order: null,
  },
  {
    step: "login",
    label: "Acceso (login)",
    routes: [
      "/login",
      "/login/:otp",
      "/login/:otp/:next",
      "/signin",
      "/otpDisabled",
      "/reset",
      "/resetDone",
    ],
    rawStatuses: ["initial_status"],
    markets: ALL,
    order: null,
  },
  {
    step: "renewal_confirmation",
    label: "Confirmación de renovación",
    routes: ["/renewal-confirmation"],
    rawStatuses: [],
    markets: ITALY,
    order: null,
  },
  {
    step: "partner_landing",
    label: "Entrada desde partner (Rastreator, Facile)",
    routes: ["/partners/rastreator", "/partners/facile"],
    rawStatuses: [],
    markets: ["ES", "IT", "IT_NATIVE"],
    order: null,
  },
  {
    step: "resume_velogica",
    label: "FR: vuelta de Velogica",
    routes: ["/resumeVelogica"],
    rawStatuses: [],
    markets: ["FR"],
    order: null,
  },
  {
    step: "other",
    label: "Otra pantalla (textos legales, no encontrada…)",
    routes: [],
    rawStatuses: [],
    markets: ALL,
    order: null,
  },
];

const BY_STEP: Record<string, StepDefinition> = STEP_CATALOG.reduce(
  (acc, def) => ({ ...acc, [def.step]: def }),
  {} as Record<string, StepDefinition>
);

/** Paso de la calculadora en el que se ve el precio, por mercado. */
export const quoteCalculatorStep = (market: Market | null): number =>
  market === "FR" ? 9 : 4;

const CALCULATOR_PATH = /^\/(?:calculator|calculatorChallenger)\/(\d+)\/?$/;

/** Rutas de la app → paso. Orden importante: lo concreto antes que lo genérico. */
const PATH_RULES: Array<[RegExp, CatalogStepId]> = [
  [/^\/(?:payment|paymentChallenger|paymentProposed|paymentNew)\/1\/?$/, "upsell"],
  [/^\/(?:payment|paymentChallenger|paymentProposed|paymentNew)\/3\/?$/, "payment"],
  [/^\/(?:payment|paymentChallenger|paymentProposed|paymentNew)\/4\/?$/, "legal_note"],
  [/^\/(?:payment|paymentChallenger|paymentProposed|paymentNew)\/5\/?$/, "signature"],
  [/^\/productQuestions\/?$/, "product_questions"],
  [/^\/medicalQuestions\/?$/, "medical_questions"],
  [/^\/notcoverage\/?$/, "rejected"],
  [/^\/telesubscription\/?$/, "telesubscription"],
  [/^\/maxCapital\/?$/, "max_capital"],
  [/^\/pause(?:AB)?\/?$/, "pause"],
  [/^\/waiting-advisor\/?$/, "waiting_advisor"],
  [/^\/confirmation\/?$/, "confirmation"],
  [/^\/coverage\/0\/?$/, "nationality"],
  [/^\/coverage\/1\/?$/, "address"],
  [/^\/coverage\/2\/?$/, "beneficiaries"],
  [/^\/coverage\/7\/?$/, "idcard"],
  [/^\/beneficiary\/?$/, "beneficiaries"],
  [/^\/beneficiaries\/designation\/[^/]+\/?$/, "beneficiaries_clause"],
  [/^\/beneficiaries\/1\/?$/, "beneficiaries_types"],
  [/^\/beneficiaries\/2\/?$/, "beneficiaries_physical_person"],
  [/^\/beneficiaries\/3\/?$/, "beneficiaries_banking_entity"],
  [/^\/beneficiaries\/4\/?$/, "beneficiaries_legal_person"],
  [/^\/beneficiaries\/5\/?$/, "beneficiaries_capital"],
  [/^\/consents\/?$/, "client_consents"],
  [/^\/card-validation\/?$/, "card_validation"],
  [/^\/waitingvalidation\/?$/, "waiting_validation"],
  [/^\/agent-confirmation\/?$/, "agent_confirmation"],
  [/^\/waitingsignature\/?$/, "waiting_signature"],
  [/^\/waiting-customer\/?$/, "waiting_customer"],
  [/^\/dashboard(?:\/[^/]*)?\/?$/, "dashboard"],
  [/^\/decision\/?$/, "decision"],
  [/^\/(?:login(?:\/.*)?|signin|otpDisabled|reset|resetDone)\/?$/, "login"],
  [/^\/renewal-confirmation\/?$/, "renewal_confirmation"],
  [/^\/partners\/(?:rastreator|facile)\/?$/, "partner_landing"],
  [/^\/resumeVelogica\/?$/, "resume_velogica"],
];

/** Rutas que solo redirigen (a /login o al paso 1): no son una pantalla. */
const REDIRECT_ONLY = /^\/(?:calculator|calculatorChallenger)?\/?$/;

export const isRedirectOnlyPath = (pathname: string): boolean =>
  REDIRECT_ONLY.test((pathname || "/").split("?")[0]);

/** Paso de la pantalla en la que está el cliente (por la ruta, sin query string). */
export const stepForPath = (pathname: string, market: Market | null): StepId => {
  const path = (pathname || "/").split("?")[0];
  const calculator = CALCULATOR_PATH.exec(path);
  if (calculator) {
    const n = parseInt(calculator[1], 10);
    return n === quoteCalculatorStep(market) ? "quote" : `calculator_${n}`;
  }
  const rule = PATH_RULES.find(([re]) => re.test(path));
  return rule ? rule[1] : "other";
};

/** Paso que corresponde a un estado del lead (el que lo nombra primero en el catálogo). */
export const stepForStatus = (status: string | null | undefined): StepId => {
  if (!status) return "other";
  const def = STEP_CATALOG.find((d) => d.rawStatuses[0] === status) ||
    STEP_CATALOG.find((d) => d.rawStatuses.includes(status));
  return def ? def.step : "other";
};

const isCalculatorStep = (step: StepId): step is CalculatorStepId =>
  /^calculator_\d+$/.test(step);

export const stepOrder = (step: StepId): number | null => {
  if (isCalculatorStep(step)) return parseInt(step.slice("calculator_".length), 10);
  return BY_STEP[step]?.order ?? null;
};

/**
 * 1 si `to` va después de `from` en el recorrido, -1 si va antes y 0 si no se
 * puede decir (misma posición o alguna pantalla lateral).
 */
export const compareSteps = (from: StepId, to: StepId): -1 | 0 | 1 => {
  const a = stepOrder(from);
  const b = stepOrder(to);
  if (a === null || b === null || a === b) return 0;
  return b > a ? 1 : -1;
};

export const stepLabel = (step: StepId): string => {
  if (isCalculatorStep(step)) {
    const n = parseInt(step.slice("calculator_".length), 10);
    return CALCULATOR_STEP_LABELS[n] ?? `Calculadora: paso ${n}`;
  }
  return BY_STEP[step]?.label ?? BY_STEP.other.label;
};

/** Mercado del build (`REACT_APP_DOMAIN_LOCALE`) en el formato del contrato. */
export const marketFromLocale = (locale: string | undefined): Market | null => {
  switch ((locale || "").toLowerCase()) {
    case "es":
      return "ES";
    case "it":
      return "IT";
    case "fr":
      return "FR";
    case "wl_es":
      return "AXA_WL";
    case "sql_es":
      return "SQL_ES";
    default:
      return null;
  }
};
